import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React, { useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { Links, Meta, Outlet, Scripts } from 'react-router';
// App before index.css: keeps component CSS ahead of Tailwind in the cascade, as src/index.tsx did.
import App from '../src/App';
import { store } from '../src/stores/store';

import { Snackbar } from '../src/components/_ui';
import { AuthProvider } from '../src/hooks';
import '../src/index.css';
import { i18n } from '../src/services/translation/i18n';

// Reproduces the old public/index.html. The ids on the icon links are kept because App.tsx
// swaps favicon/touch icon by id at runtime, and #root must exist because modal components
// call Modal.setAppElement('#root') at module scope.
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en'>
      <head>
        <meta charSet='utf-8' />
        <link rel='icon' id='favicon' href='/favicon.ico' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <meta name='theme-color' content='#000000' />
        <meta name='description' content='' />
        <link rel='apple-touch-icon' id='touchicon' href='/logo192.png' />
        <link rel='manifest' href='/manifest.json' />
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='' />
        <link
          href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
          rel='stylesheet'
        />
        <Meta />
        <Links />
        <title></title>
      </head>
      <body>
        <noscript>You need to enable JavaScript to run this app.</noscript>
        <div id='root'>{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

// Same provider tree as the old src/index.tsx, minus BrowserRouter (the framework provides
// the router). App is handed to the pathless layout routes/app.tsx through the Outlet context so
// its descendant <Routes> sit under a "*" parent, while the whole src/ graph (and its CSS order)
// stays in the root chunk. Snackbar stays outside StrictMode.
export default function Root() {
  // React Router re-renders this route component on every navigation. Under CRA this provider tree
  // lived in src/index.tsx and never re-rendered on a route change; here a re-render would recreate
  // AuthProvider's context value (a new object per render), cascade into App, and remount its
  // ProtectedLayout/Layout closures (sidebar, header and logo flicker). Memoising the tree keeps the
  // providers out of the navigation render path; <Outlet/> subscribes to route context itself and
  // still updates. The trigger is React Router's withComponentProps wrapper around route
  // components: it re-renders them on every router state change (navigation start and end,
  // revalidation), not only on location changes. The deps are [], so any hook Root reads later
  // must be added to them; prefer putting new root-level state in a provider instead.
  return useMemo(
    () => (
      <PostHogProvider client={posthog}>
        <Provider store={store}>
          <React.StrictMode>
            <AuthProvider>
              <I18nextProvider i18n={i18n}>
                <Outlet context={App} />
              </I18nextProvider>
            </AuthProvider>
          </React.StrictMode>
          <Snackbar />
        </Provider>
      </PostHogProvider>
    ),
    [],
  );
}

// SPA mode prerenders the root in Node at build time. A root clientLoader makes that prerender
// emit HydrateFallback (an empty #root) instead of the app tree, which reads window during
// render (useAuth). The loader never needs to run again.
export async function clientLoader() {
  return null;
}

export const shouldRevalidate = () => false;

export function HydrateFallback() {
  return null;
}

// Uncaught render errors leave a blank page, as they did under CRA.
export function ErrorBoundary() {
  return null;
}
